import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Ceiling we wait before unmounting, with headroom for the frame the browser
 * needs to start the animation.
 *
 * The longest exit in Tessra is `duration-base` (200ms) — Sheet closes on
 * `data-[state=closed]:duration-base`, while Dialog and AlertDialog use
 * `duration-fast`. 260 clears the slowest of them.
 */
const EXIT_ANIMATION_CEILING_MS = 260;

/**
 * Manages modal open/close/exit lifecycle so a close animation can play before
 * the content unmounts.
 *
 * `isMounted` and `isOpen` diverge during the closing phase: `isMounted` keeps
 * the component in the DOM so the exit animation can run, while `isOpen` is
 * already false to trigger it. Once the phase ends, both are false and the
 * component unmounts — resetting its internal state automatically.
 *
 * `close()` schedules that unmount itself, on a timer rather than on the exit
 * animation's `animationend`. That looks like the cruder option and is the
 * deliberate one:
 *
 * - Radix removes dialog content as soon as `open` is false in any environment
 *   with no CSS animations (jsdom, happy-dom), so there is no node left to fire
 *   `animationend` on. A modal wired that way never unmounts in tests, and
 *   `useModalState` stays in `closing` forever.
 * - That is not hypothetical: was exactly this failure in production
 *   form. The instance was reused on the next `open()` with its staged edits
 *   still populated, duplicating a just-persisted member. It was fixed then by
 *   calling `onExited` in the same tick as `onClose` — which unmounted instantly
 *   and cost the exit animation, the bug this replaces.
 *
 * A timer keeps the unmount guaranteed while letting the animation play. The
 * cost is ~100ms of extra mounted-but-invisible time, which nothing observes.
 *
 * `onExited` is still exported for MUI modals that drive it from
 * `slotProps.transition.onExited`; calling it early just unmounts sooner.
 */
export function useModalState() {
  const [state, setState] = useState<'closed' | 'open' | 'closing'>('closed');
  const [instance, setInstance] = useState(0);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExitTimer = useCallback(() => {
    if (exitTimer.current !== null) {
      clearTimeout(exitTimer.current);
      exitTimer.current = null;
    }
  }, []);

  useEffect(() => clearExitTimer, [clearExitTimer]);

  const open = useCallback(() => {
    clearExitTimer();
    // Reopening inside the exit window has to remount, not resume: a modal that
    // resets only on unmount (staged members, wizard step, form values) would
    // otherwise come back mid-edit — the failure re-entering through
    // the timer that replaced it.
    //
    // Done with a changing key rather than by forcing the unmount to commit
    // first. Forcing it (`flushSync(() => setState('closed'))`) does remount,
    // but hosts observe that transient `isMounted: false` and act on it — both
    // edit tables clear their selected row in an effect keyed on it, so the
    // guard `isMounted && rowToEdit` then had nothing to render and the reopen
    // click did nothing at all.
    setInstance((n) => n + 1);
    setState('open');
  }, [clearExitTimer]);

  const close = useCallback(() => {
    clearExitTimer();
    setState('closing');
    exitTimer.current = setTimeout(() => {
      exitTimer.current = null;
      setState('closed');
    }, EXIT_ANIMATION_CEILING_MS);
  }, [clearExitTimer]);

  const onExited = useCallback(() => {
    clearExitTimer();
    setState('closed');
  }, [clearExitTimer]);

  return {
    /** Whether the modal content should be mounted. */
    isMounted: state !== 'closed',
    /** Whether the modal is visible (pass to the modal's `open` prop). */
    isOpen: state === 'open',
    open,
    close,
    /**
     * Pass as `key` on the modal element. Changes on every `open()`, so a reopen
     * inside the exit window mounts a fresh instance instead of resuming the old
     * one — which matters for any modal that resets only by unmounting (staged
     * members, a wizard step, populated form values).
     */
    instanceKey: instance,
    /**
     * Unmount now, skipping the remaining exit animation. Only needed for MUI
     * modals via `slotProps.transition.onExited` — `close()` already schedules
     * the unmount, so a Radix dialog should not call this.
     */
    onExited,
  };
}
